import type { ISbStoriesParams } from 'storyblok-js-client';
import type { RouteLocationNormalized } from 'vue-router';
import { isStoryblokInspectorEnabled } from './inspector';

export function getParams<T extends ISbStoriesParams>(params?: T, locale?: string) {
  const config = useRuntimeConfig();
  const route = useRoute();

  // Determine version of the story to load
  const loadDraft =
    config.public.IS_DRAFT || route.query.draft || isStoryblokPreview() || isStoryblokInspectorEnabled();
  const version = loadDraft ? 'draft' : 'published';

  // Determine language of the story to load
  // Since the formal code is nl-fm, we need to replace the formal with fm
  // when the language is be in our front-end match it with the nl language in storyblok
  // This since we don't make a separation in locale in storyblok, only on story level if necessary
  let storyblokLanguage = locale?.includes('formal') ? 'nl-fm' : 'nl';

  // if the route has /studenten, then do not use formal
  if (route.path.includes('/studenten')) {
    storyblokLanguage = storyblokLanguage.replace('-fm', '');
  }

  function stringifyRelations(relations: string[] | string) {
    return Array.isArray(relations) ? relations.join(',') : relations;
  }

  return {
    ...(params || {}),
    version,
    language: storyblokLanguage,
    ...(params?.resolve_relations && {
      resolve_relations: stringifyRelations(params.resolve_relations),
    }),
  } satisfies ISbStoriesParams;
}

/**
 * Check if the cache is disabled, defaults to true if the stage is not production or test.
 *
 * A `?draft` request must always be served fresh: the back-end's Storyblok cache is only told to skip
 * via `X-Storyblok-No-Cache`, so the draft version alone is not enough to bypass it.
 */
export function getIsCacheDisabled() {
  const config = useRuntimeConfig();
  const route = useRoute();

  return Boolean(
    !['production', 'test'].includes(config.public.STAGE as string) ||
    import.meta.env.STORYBLOK_DISABLE_CACHE ||
    route.query.draft ||
    isStoryblokPreview(route),
  );
}

export function isStoryblokPreview(route?: RouteLocationNormalized) {
  const currentRoute = route || useRoute();

  return Boolean(currentRoute.query._storyblok);
}

export function isInStoryblokVisualEditor() {
  return import.meta.client && window.top !== window.self && isStoryblokPreview();
}
